import ConfirmationModal from 'components/ui/ConfirmationModal/confirmation-modal';
import Container from 'components/ui/Container';
import Modal from 'components/ui/Modal/modal';
import PrimaryButton from 'components/ui/PrimaryButton';
import SecondaryButton from 'components/ui/SecondaryButton';
import { useFormik } from 'formik';
import useToggle from 'hooks/useToggle';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  IInternalUserInput,
  internalUserInitialValues,
  internalUserSchemaValidations
} from './schema';

import {
  useCreateInternalUserMutation,
  useUnBlockInternalUserMutation,
  useUpdateInternalUserMutation
} from 'redux/reducers/internal-user';
import CsvUploadForm from '../Trainee/CsvUploadForm';
import InternalUserForm from './InternalUserForm';
import InternalUserTable from './InternalUserTable';
import CustomSelect from 'components/ui/Editor/CustomSelect';
import { useDecodeToken } from 'hooks/useStorage';

const InternalUser = () => {
  const timerRef = React.useRef<NodeJS.Timeout>();
  const navigate = useNavigate();
  const [showQuery, setShowQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formModal, setFormModal] = useToggle(false);
  const [csvFormModal, setCsvFormModal] = useToggle(false);
  const [unblockModal, setUnblockModal] = useToggle(false);
  const [unBlockUserId, setUnBlockUserId] = useState<number>();
  const [unblockUser, { isLoading: isUnblocking }] = useUnBlockInternalUserMutation();
  const [userType, setUserType] = useState<any>("")

  const onUnblock = (id: number) => {
    setUnblockModal(true);
    setUnBlockUserId(id);
  };

  const onUnblockConfirm = () => {
    unBlockUserId &&
      unblockUser(unBlockUserId)
        .unwrap()
        .then(() => {
          toast.success('Unblocked Successfully');
          setUnblockModal(false);
        })
        .catch((err: any) => {
          toast.error('Error in unblocking');
        });
  };
  const [internalUserInitialFormState, setinternalUserInitialForm] =
    useState(internalUserInitialValues);
  const [deleteInternalUserState, setDeletingInternalUser] = useState<IInternalUserInput | null>(
    null
  );

  const [createInternalUser, { isLoading: isCreating }] = useCreateInternalUserMutation();
  const [updateInternalUser, { isLoading: isUpdating }] = useUpdateInternalUserMutation();

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: internalUserSchemaValidations,
    initialValues: internalUserInitialFormState,
    onSubmit: (values) => {
      let executeFunc = createInternalUser;
      if (values.id) {
        executeFunc = updateInternalUser;
      }
      executeFunc({
        ...values,
        middle_name: values.middle_name === '' ? null : values.middle_name,
        mobile_number: values.mobile_number.toString(),
        role: values.role
      })
        .unwrap()
        .then((data: any) => {
          toast.success(data.message);
          setinternalUserInitialForm(internalUserInitialValues);
          setFormModal();
        })
        .catch((err: any) => {
          toast.error(err?.data?.message);
        });
    }
  });

  const onEditClicked = (data: IInternalUserInput) => {
    setinternalUserInitialForm({
      ...data,
      role: data.user_type === '1' ? 'INTERNAL' : 'EXTERNAL'
    });

    setFormModal();
  };

  const onViewClick = (data: IInternalUserInput) => {
    const userType = data?.user_type;
    userType == '1'
      ? navigate(`/internal-user-management/internal/${data.id}`)
      : navigate(`/external-user-management/external-profile/${data.id}`);
  };

  const closeModal = () => {
    setFormModal();
    setinternalUserInitialForm(internalUserInitialValues);
  };
  const decodedToken: any = useDecodeToken();

  const isSuperAdmin = decodedToken?.IsSuperUser == 'True';


  return (
    <Container title="Resource Person" subTitle="List of all resource person">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 w-100 mb-3">
        <PrimaryButton
          title={internalUserInitialFormState.id ? 'Edit Admin User' : 'Create Admin User'}
          iconName="ri-add-line align-bottom me-1"
          onClick={setFormModal}
          type="button"
        />

        <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
          <div style={{ minWidth: '200px' }}>
            <CustomSelect
              name="usertype"
              containerClassName="mb-0"
              placeholder="User type"
              options={[
                { label: 'All User Types', value: '' },
                { label: 'Internal', value: '1' },
                { label: 'External', value: '3' }
              ]}
              value={userType}
              onChange={(value: any) => {
                setUserType(value);
              }}
              formik={formik}
            />
          </div>

          <div className="search-box">
            <input
              type="text"
              className="form-control search"
              placeholder="Search..."
              name="key"
              value={showQuery}
              onChange={(e) => {
                setShowQuery(e.target.value);
                clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                  setSearchQuery(e.target.value);
                }, 2000);
              }}
            />
            <i className="ri-search-line search-icon" />
          </div>
        </div>
      </div>

      <InternalUserTable
        onEditClicked={onEditClicked}
        onViewClick={onViewClick}
        searchQuery={searchQuery}
        onUnBlock={onUnblock}
        userType={userType}
      />
      <CsvUploadForm csvFormModal={csvFormModal} setCsvFormModal={setCsvFormModal} isAdmin={true} />
      <form onSubmit={formik.handleSubmit}>
        <Modal
          title={internalUserInitialFormState.id ? 'Edit Admin User' : 'Create Admin User'}
          isModalOpen={formModal}
          closeModal={closeModal}
          renderFooter={() => (
            <div className="hstack gap-2 justify-content-end">
              <SecondaryButton
                onClick={closeModal}
                title="Close"
                isLoading={isUpdating || isCreating}
              />
              <PrimaryButton
                title={internalUserInitialFormState.id ? 'Update' : 'Create'}
                iconName="ri-add-line align-bottom me-1"
                type="submit"
                isLoading={isUpdating || isCreating}
              />
            </div>
          )}
        >
          <InternalUserForm formik={formik} />
        </Modal>
      </form>
      {unBlockUserId && (
        <ConfirmationModal
          isOpen={unblockModal}
          onClose={setUnblockModal}
          onConfirm={onUnblockConfirm}
          title={`Unblock User`}
          btnText={'Unblock'}
          isLoading={isUnblocking}
        >
          <>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>Are you Sure ?</h4>
              <p className="text-muted ">you want to unblock this user?</p>
            </div>
          </>
        </ConfirmationModal>
      )}
    </Container>
  );
};

export default InternalUser;
